// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Alert, Box, Button, Center, LoadingOverlay, Text } from '@mantine/core';
import { FC, useEffect, useState } from 'react';
import { openModal } from '@mantine/modals';
import { IconAlertTriangle, IconSend } from '@tabler/icons';
import { BookitupNotification } from '../../../../Types/LogT';
import { MailS } from '../../../../Service/MailS';
import { MailgunDeliveryFailureReportDTO } from '../../../../Types/MailT';
import { isDefined } from '../../../../Service/ValidationS';
import LinkText from '../../../../Atoms/LinkText';
import BookitupAlert from '../../../../Atoms/BookitupAlert';

const EmailDeliveryFailure: FC<{ notification: BookitupNotification }> = ({ notification }) => {
  const { entityId, value } = notification;
  const { subject, recipient } = JSON.parse(value ?? '');

  const onClick = () =>
    openModal({
      title: (
        <Text weight="bolder" size="xl">
          E-Mail-Bericht
        </Text>
      ),
      // eslint-disable-next-line react/jsx-props-no-spreading
      children: <ResendFailedMailModal {...{ subject, recipient, reportId: entityId }} />,
    });

  return (
    <Text>
      Die Nachricht mit dem Betreff{' '}
      <Text span weight="bolder">
        {subject}
      </Text>{' '}
      für{' '}
      <Text span weight="bolder">
        {recipient}
      </Text>{' '}
      konnte derzeit nicht zugestellt werden.{'  '}
      <LinkText span onClick={onClick} weight="bolder">
        Klicke hier
      </LinkText>
      {'  '}
      für weitere Details.
    </Text>
  );
};

interface Props {
  subject: string;
  recipient: string;
  reportId: string;
}

const ResendFailedMailModal: FC<Props> = ({ subject, recipient, reportId }) => {
  const [failureReport, setReport] = useState<MailgunDeliveryFailureReportDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const { description, message, expired, resended } = failureReport ?? {};
  const resendPossible = !expired && !resended;

  useEffect(() => {
    MailS.getFailureReportDescription(reportId)
      .then(setReport)
      .finally(() => setLoading(false));
  }, [reportId]);

  const resend = async () => {
    if (failureReport) {
      setLoading(true);
      const ok = await MailS.resendMessage(reportId);
      if (ok) {
        setReport({ ...failureReport, resended: true });
      }
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} />
      <>
        {resended && (
          <BookitupAlert title="Erneuter E-Mail-Versand" mb="sm">
            <Text>
              Die E-Mail wird erneut versucht zuzustellen. Wenn das Problem dauerhaft besteht, wende dich bitte an
              unseren Support.
            </Text>
          </BookitupAlert>
        )}
        {!resended && expired && (
          <Alert
            mb="sm"
            icon={<IconAlertTriangle color="white" />}
            sx={(theme) => ({ backgroundColor: theme.colors.orange[3] })}
          >
            <Text color="white">
              Die E-Mail-Versand ist zu lange her. Versende die E-Mail erneut über den normalen Weg.
            </Text>
          </Alert>
        )}
        <Text color="dimmed" size="xs">
          Betreff
        </Text>
        <Text>{subject}</Text>
        <Text mt="md" color="dimmed" size="xs">
          Empfänger
        </Text>
        <Text>{recipient}</Text>
        {isDefined(message) && (
          <>
            <Text mt="md" color="dimmed" size="xs">
              Nachricht{' '}
            </Text>
            <Box>{message}</Box>
          </>
        )}
        {isDefined(description) && (
          <>
            <Text mt="md" color="dimmed" size="xs">
              Problem{' '}
            </Text>
            <Box>{description}</Box>
          </>
        )}
        {resendPossible && (
          <Center mt={40}>
            <Button leftIcon={<IconSend />} onClick={resend} sx={{ width: 250 }}>
              E-Mail erneut senden
            </Button>
          </Center>
        )}
      </>
    </>
  );
};

export default EmailDeliveryFailure;
